/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import Checkbox from '../Checkbox';
import Image from '../Image';
import Input from '../Input';
import Radio from '../Radio';
import Select from '../Select';
import ContainerTemplate from '../ContainerTemplate';
import Separator from '../Separator';
import Switcher from '../Switcher';
import Chart from '../Chart';
import { getVariableValue, setVariableValue } from '../../utils/globalVariableHelper';
import { isNodeChild } from '../../utils/nodeTreeUtils';
import Text from '../Text';
import moment from 'moment';
import { history } from '../../configureStore';
import merge from 'lodash.merge';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { getCorrespondingScheme, parseIfJSON, handleOpenUrl, executeFunction, getNestedValue, isClickInsidePortal,
  replacePlaceholdersWithVariables, replacePlaceholdersWithVariablesV2,  combineClassNames, getSizesBasedOnScreen,
 } from '../../utils/base';
import { pageRequest, submitFormRequest, mergeStyleRequest, updateVariableRequest, actionRequest } from '../../store/page/actions';
import { StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk, NodeTypeScheme, Breakpoints, ActionChunk } from '../../types/base';
import generateComponentStyles from '../../utils/styleHelper';
import useScrollToTopHook from '../../utils/scrollToTopHook';
import { rowSizeGlobal, colCountGlobal } from '../../config/config';

import * as style from './Container.module.scss';


const componentMapping = {
  checkbox: Checkbox,
  image: Image,
  input: Input,
  radio: Radio,
  select: Select,
  separator: Separator,
  switcher: Switcher,
  chart: Chart,
  text: Text,
};

const StyledContainer = styled.div<{
  $shape?: any;
  $baseScreenResolution?: any;
  $skipStates?: boolean;
}>`
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates)}
`;

// Utility function to generate CSS classes with hover styles for targeted elements
const generateHoverClass = (elementId: string, hoverUpdates: any[], items?: any) => {
  if (!hoverUpdates?.length) return '';
  
  const hoverStyleUpdates = hoverUpdates?.filter((update): update is StyleUpdateChunk => update && 'style' in update) || [];
  
  // Only handle style updates that target specific child elements (have object_id and target a child)
  const targetedStyleUpdates = hoverStyleUpdates.filter(update => 
    update.object_id && update.object_id !== elementId
  );
  
  if (!targetedStyleUpdates.length) return '';
  
  const className = `hover-class-${elementId.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
  const mainElementSelector = `#${elementId.replace(/[^a-zA-Z0-9-_]/g, '\\\\$&')}`;
  
  // Generate CSS for hover state
  let css = '';
  
  if (targetedStyleUpdates.length) {
    targetedStyleUpdates.forEach((update) => {
      const { style: updateStyle, object_id: targetId } = update;
      // Use generateComponentStyles to properly convert style object to CSS
      const generatedStyles = generateComponentStyles({ display: updateStyle?.visibility === 'visible' ? 'flex' : undefined, ...updateStyle }, undefined, true);
      
      if (generatedStyles && typeof generatedStyles === 'object') {
        const cssProperties = Object.entries(generatedStyles)
          .filter(([key, value]) => value !== null && value !== undefined)
          .map(([key, value]) => `    ${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`)
          .join('\n');
        
        if (cssProperties) {
          
          // Target specific child element with data-id, nested inside main element hover
          const targetSelector = `[data-id="${targetId.replace(/"/g, '\\"')}"]`;
          css += `
  .${className}:hover ${targetSelector} {
${cssProperties}
  }`;
        }
      }
    });
  }
  
  // Inject CSS into document head
  const styleId = `hover-style-${elementId.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.textContent = css;
  } else {
    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = css;
    document.head.appendChild(styleElement);
  }
  
  return className;
};

const GridContainerComponent: React.FC<NodeTypeScheme> = (obj) => {
  const {
    id, items, style: stDraft, component,
    events: eventsDraft, dataLake,
    parentIndex,
    pageSettings,
    styleChunks,
    activeState: activeStateDraft,
    nestedTable,
    skeleton: renderSkeleton2,
    parentLoading,
    topLayerNode,
    hash,
    enableLocalShimmer,
    cache,
    settings,
  } = obj || {};
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { style: styleDraft, events, activeState, ref: realRef } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ ref, style: stDraft, events: eventsDraft, activeState: activeStateDraft }, dataLake.data) : { ref: undefined, style: stDraft, events: eventsDraft, activeState: activeStateDraft };
  // Merge active state styles with base style
  const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
    const { indexes, refs } = chunks || {};
    let mergedStyle = style;
    
    // Apply index-based styles first
    if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
      mergedStyle = merge({}, mergedStyle, indexes[id][index]);
    }
    
    // Apply ref-based updates at the end
    if (refs && realRef) {
      const refUpdate = refs[realRef];
      if (refUpdate?.type && refUpdate.type === 'container') {
        mergedStyle = merge({}, mergedStyle, refUpdate?.update);
      }
    }
    
    return mergedStyle;
  }, [id, realRef]);


  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (activeState === 'loading' || parentLoading) ? cache : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, activeState, enableLocalShimmer, parentLoading]);

  // First useMemo to merge active state styles with base style
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (activeState && styleDraft?.states?.[activeState]) {
      mergedStyle = merge({}, styleDraft, styleDraft.states[activeState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[activeState]) {
          acc[breakpoint] = merge({}, typedBreakpointStyle, typedBreakpointStyle.states[activeState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    return mergedStyle;
  }, [styleDraft, activeState]);

    // Use the custom hook
  useScrollToTopHook(containerRef, hash);

  // Second useMemo to merge styles with chunks based on parentIndex
  const st = useMemo(() => {
    return mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex);
  }, [mergedStyleWithState, styleChunks, parentIndex]);
  const { row: stickyRows } = dataLake?.settings?.pin || {};
  const isSticky = parentIndex < stickyRows;
  const {
    on_click: clickUpdates,
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_outclick: outclickUpdates,
  } = events || {};

  const { cols: colCount, rows: rowCount } = st || {};
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const variableData = useSelector(selectVariableData);
  const localVariableData = useSelector(selectLocalVariableData);
  const dispatch = useDispatch();
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const internalFns = {
    $setVariable: (key: string, value: any) => {
      setVariableValue(key, value, (localKey, localValue) => {
        dispatch(updateVariableRequest({ variable: localKey, value: localValue }));
      });
    },
    $getVariable: (key: string) => {
      return getVariableValue(key, variableData, localVariableData);
    },
    $moment: (...args: any[]) => moment(...args)
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id 
      }, filterLoadingState));
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    // for container click block requests but keep all other
    if (activeState === 'disabled' || activeState === 'loading') {
      e.stopPropagation();
      return
    };
    if (clickUpdates) {
      e.stopPropagation();
      const styleUpdates = clickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = clickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = clickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = clickUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
        handleActionChunk(actionUpdates, filterLoadingState);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id,
             },
             filterLoadingState
            ));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ 
              value, 
              delay, 
              silent, 
              componentId: id 
            },
          filterLoadingState
          ));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  // Recursive function to check if a target node is a child of the current container
  const isTargetNodeChild = useCallback((targetId: string): boolean => {

    return isNodeChild(targetId, items);
  }, [items]);

  const handleHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      
      // Only dispatch style updates that are NOT handled by CSS hover classes
      // (i.e., updates that don't target specific child elements or target the container itself)
      const styleUpdatesForDispatch = styleUpdates.filter(update => {
        // Exclude if object_id matches {{...}} or contains {{...}} pattern
        if (/\{\{.*\}\}/.test(update.object_id)) return true;
        // If no object_id, it targets the container itself
        if (!update.object_id) return true;
        
        // If object_id matches current container, include it
        if (update.object_id === (realRef || id)) return true;
        
        // If object_id targets a child node, exclude it (let CSS handle it)
        if (isTargetNodeChild(update.object_id)) return false;
        
        // Otherwise, include it (might target parent or sibling)
        return true;
      });
      
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdatesForDispatch.length) {
        dispatch(mergeStyleRequest(styleUpdatesForDispatch, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  const handleUnhover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== st?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = st?.visibility; // Set the flag to true
      const styleUpdates = launchUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            // const newTimeout = setTimeout(() => {
            //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
            // }, config?.delay * 1000 || 0);

            // timeoutsRef.current.push(newTimeout);
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, st?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
   if (!outclickUpdates?.length) return undefined;
    
    const handleUnclick = (e: MouseEvent) => {
      if (activeState === 'disabled' || activeState === 'loading') return;
      if (
        outclickUpdates?.length &&
        containerRef.current && 
        !containerRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        // Execute function updates
        if (filteredUpdates.functionUpdates.length) {
          filteredUpdates.functionUpdates.forEach((update) => {
            if (update?.function) {
              executeFunction(update.function, undefined, internalFns);
            }
          });
        }
        
        // Apply style updates
        if (filteredUpdates.styleUpdates.length) {
          dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex, dataLake?.data));
        }
        
        // Handle action updates
        if (filteredUpdates.actionUpdates.length) {
          handleActionChunk(filteredUpdates.actionUpdates);
        }
        
        // Process request updates
        if (filteredUpdates.requestUpdates.length) {
          filteredUpdates.requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ 
                value, 
                silent, 
                delay, 
                componentId: id 
              }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      }
    };
    
    document.addEventListener('click', handleUnclick);
    return () => {
      document.removeEventListener('click', handleUnclick);
    };
  }, [
    parentIndex, 
    activeState, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length
  ]);

  // Generate hover class name when component loads or id changes
  const hoverClassName = useMemo(() => {
    return generateHoverClass(realRef || id, hoverUpdates);
  }, [id, realRef, hoverUpdates]);

  // Cleanup hover styles when component unmounts
  useEffect(() => {
    if (!id) return undefined;
    const currentId = id;
    return () => {
      // Check if any other elements with the same data-id still exist in the DOM
      const elementsWithSameId = document.querySelectorAll(`[data-id="${currentId}"]`);
      
      // Only remove the hover styles if no other elements with this id exist
      if (elementsWithSameId?.length === 0) {
        const styleId = `hover-style-${currentId.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      }
    };
  }, [id]);

  return (
    <StyledContainer
      ref={containerRef}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      style={{
        gridTemplateColumns: `repeat(${colCount || colCountGlobal}, 1fr)`,
        gridTemplateRows: rowCount ? `repeat(${rowCount}, 1fr)` : undefined,
        gridAutoRows: rowCount ? undefined : `${rowSizeGlobal}px`,
        position: isSticky ? 'sticky' : undefined,
        maxWidth: topLayerNode ? '100vw' : undefined,
        top: isSticky ? 0 : undefined,
        zIndex: isSticky ? `${99999999 - parentIndex}` : undefined,
        visibility: renderSceleton && st ? 'hidden' : undefined,
      }}
      data-id={realRef || id}
      $shape={st}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={activeState === 'disabled' || activeState === 'loading'}
      data-component={component}
      onClick={handleClick}
      onMouseEnter={hoverUpdates?.length ? handleHover : undefined}
      onMouseLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      className={hoverClassName}
    >
      {items && items.map((item, index) => {
        const Component = componentMapping[item.component];
        const commonProps = {
          dataLake: getCorrespondingScheme(item, obj, dataLake, index, pageSettings?.variables, parentIndex),
          parentIndex: parentIndex,
          pageSettings,
          styleChunks,
          nestedTable,
          parentStyle: st,
          parentLoading: activeState === 'loading' || parentLoading,
          enableLocalShimmer,
        };

        if (item.component === 'container' && item.type === 'table-body-row' && item.dynamic) {
          const replaced = replacePlaceholdersWithVariables(commonProps?.dataLake?.settings?.source, pageSettings?.variables);
          const parseSourceIfExist = parseIfJSON(replaced);
          return Array.isArray(parseSourceIfExist) ? parseSourceIfExist?.map((sourceItem, sourceIndex) => {
            const dynamicProps = {
              dataLake: getCorrespondingScheme(item, obj, dataLake, sourceIndex, pageSettings?.variables, parentIndex),
              parentIndex: sourceIndex,
              styleChunks,
              nestedTable: true,
              parentLoading: activeState === 'loading' || parentLoading,
              enableLocalShimmer,
            };
            if (Component) {
              return <Component key={`${item.id}::${sourceIndex}`} {...item} {...dynamicProps} parentRef={containerRef} />;
            } if (item.component === 'container') {
              return <ContainerTemplate key={`${item.id}::${sourceIndex}`} {...item} {...dynamicProps} hash={hash} />;
            } if (item.component === 'table') {
              return <ContainerTemplate key={`${item.id}::${sourceIndex}`} {...item} {...dynamicProps} hash={hash} />;
            }
            return null;
          }) : null;
        }

        if (Component) {
          return <Component key={item.id} {...item} {...commonProps} parentRef={containerRef} />;
        } if (item.component === 'container') {
          return <ContainerTemplate key={item.id} {...item} {...commonProps} hash={hash} />;
        } if (item.component === 'table') {
          return <ContainerTemplate key={item.id} {...item} {...commonProps} hash={hash} />;
        }
        return null;
      })}
      {(renderSceleton && st) ? (
        <ContainerSkeleton
          style={{...st, display: st.display === 'grid' ? 'flex' : st.display }}
          baseScreenResolution={baseScreenResolution}
          skipStates={activeState === 'disabled' || activeState === 'loading'}
          nested={true}
        />
      ) : null}
    </StyledContainer>
  );

}

export default GridContainerComponent;
